<template>
    <div id="chartdiv" style="width: 100%; height: 500px"></div>
</template>

<script>
import * as am4core from "@amcharts/amcharts4/core";
import * as am4charts from "@amcharts/amcharts4/charts";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";

export default {
    props: ["data"],
    mounted() {
        this.fetchDataAndGenerateChart();
    },
    watch: {
        data: {
            deep: true,
            handler: "fetchDataAndGenerateChart",
        },
    },
    methods: {
        fetchDataAndGenerateChart() {
            this.generateChart(this.data);
        },
        generateChart(data) {
            am4core.useTheme(am4themes_animated);
            if (data && data.length > 0) {
                // Filter out 'Others' category
                const filteredData = data.filter(
                    (item) => item.ekspedisi !== "Others"
                );

                // Extract 'Others' data
                const othersData = data.find(
                    (item) => item.ekspedisi === "Others"
                );

                // Delay chart creation until the DOM is fully loaded
                this.$nextTick(() => {
                    // Create chart instance
                    let chart = am4core.create("chartdiv", am4charts.XYChart);

                    // Add filtered data
                    chart.data = filteredData.map((item, index) => ({
                        ekspedisi: item.ekspedisi,
                        jumlah_muat: item.jumlah_muat,
                        plant_alokasi: item.plant_alokasi,
                        color: this.getColumnColor(index),
                    }));

                    // Include 'Others' category
                    if (othersData) {
                        chart.data.push({
                            ekspedisi: othersData.ekspedisi,
                            jumlah_muat: othersData.details.reduce(
                                (acc, cur) => acc + cur.jumlah_muat,
                                0
                            ),
                            plant_alokasi: othersData.details.reduce(
                                (acc, cur) => acc + cur.plant_alokasi,
                                0
                            ),
                            details: othersData.details
                                .filter((detail) => detail.ekspedisi !== null)
                                .map(
                                    (detail) =>
                                        `${detail.ekspedisi}: ${detail.jumlah_muat}, ${detail.plant_alokasi}`
                                )
                                .join("\n"),
                            color: "#2D6FEB",
                        });
                    }

                    // Create axes
                    let categoryAxis = chart.xAxes.push(
                        new am4charts.CategoryAxis()
                    );
                    categoryAxis.dataFields.category = "ekspedisi";
                    categoryAxis.renderer.grid.template.location = 0;
                    categoryAxis.renderer.minGridDistance = 30;
                    categoryAxis.start = 0;
                    categoryAxis.end = 1;
                    categoryAxis.keepSelection = true;

                    // Create value axis
                    const valueAxis = chart.yAxes.push(
                        new am4charts.ValueAxis()
                    );
                    valueAxis.title.text = "";

                    // Set the visible maximum limit on the y-axis to 100
                    valueAxis.min = 0;
                    valueAxis.extraMax = 0.1; // Add extra space at the top

                    // Create series for plant_alokasi
                    let series1 = chart.series.push(
                        new am4charts.ColumnSeries()
                    );
                    series1.dataFields.valueY = "plant_alokasi";
                    series1.dataFields.categoryX = "ekspedisi";
                    series1.name = "Plant Alokasi";
                    series1.columns.template.propertyFields.fill = "color";

                    // Set column width
                    series1.columns.template.width = am4core.percent(80); // Adjust the width here

                    // Set column properties
                    let columnTemplate1 = series1.columns.template;
                    columnTemplate1.strokeWidth = 0;
                    columnTemplate1.strokeOpacity = 0;
                    columnTemplate1.column.cornerRadiusTopLeft = 2;
                    columnTemplate1.column.cornerRadiusTopRight = 2;

                    // label atas cluster kiri
                    let labelBullet1 = series1.bullets.push(
                        new am4charts.LabelBullet()
                    );
                    labelBullet1.label.text = "[bold]■ {valueY}";
                    labelBullet1.label.fontSize = 15;
                    labelBullet1.label.fill = am4core.color("#2830fc"); // Black font color for visibility
                    labelBullet1.dy = -5; // Position label above the bar

                    // label bawah cluster kiri
                    let labelBullet1Bottom = series1.bullets.push(
                        new am4charts.LabelBullet()
                    );
                    labelBullet1Bottom.label.text = "{valueY}";
                    labelBullet1Bottom.label.fontSize = 15;
                    labelBullet1Bottom.label.fill = am4core.color("#ffffff"); // Black font color for visibility
                    labelBullet1Bottom.locationY = 0.5; // Position label in the middle of the bar

                    // Create series for jumlah_muat
                    let series2 = chart.series.push(
                        new am4charts.ColumnSeries()
                    );
                    series2.dataFields.valueY = "jumlah_muat";
                    series2.dataFields.categoryX = "ekspedisi";
                    series2.name = "Jumlah Muat";
                    series2.columns.template.propertyFields.fill = "color";

                    // Set column width
                    series2.columns.template.width = am4core.percent(90); // Adjust the width here

                    // Set column properties
                    let columnTemplate2 = series2.columns.template;
                    columnTemplate2.strokeWidth = 0;
                    columnTemplate2.strokeOpacity = 0;
                    columnTemplate2.column.cornerRadiusTopLeft = 2;
                    columnTemplate2.column.cornerRadiusTopRight = 2;

                    // label atas cluster kanan
                    let labelBullet2 = series2.bullets.push(
                        new am4charts.LabelBullet()
                    );
                    labelBullet2.label.text = "[bold]■ {valueY}";
                    labelBullet2.label.fontSize = 15;
                    labelBullet2.label.fill = am4core.color("#4d4d4d"); // Black font color for visibility
                    labelBullet2.dy = -5; // Position label above the bar

                    // label bawah cluster kanan
                    let labelBullet2Bottom = series2.bullets.push(
                        new am4charts.LabelBullet()
                    );
                    labelBullet2Bottom.label.text = "{valueY}";
                    labelBullet2Bottom.label.fontSize = 15;
                    labelBullet2Bottom.label.fill = am4core.color("#ffffff"); // Black font color for visibility
                    labelBullet2Bottom.locationY = 0.5; // Position label in the middle of the bar

                    // Add legends
                    chart.legend = new am4charts.Legend();
                    chart.legend.labels.template.fontSize = 15;
                    chart.legend.itemContainers.template.padding = 5;
                    chart.legend.useDefaultMarker = true;
                    chart.legend.position = "top";
                    chart.legend.contentAlign = "right";
                    chart.legend.valign = "right";
                    chart.legend.scrollable = true;

                    // Customize legend labels
                    chart.legend.labels.template.text =
                        "[font-size: 15px]{name}";
                    chart.legend.data = [
                        { name: "Total Plant", fill: am4core.color("#2830fc") },
                        {
                            name: "Total Delivery",
                            fill: am4core.color("#4d4d4d"),
                        },
                    ];
                    let marker =
                        chart.legend.markers.template.children.getIndex(0);
                    marker.cornerRadius(0, 0, 0, 0);
                    marker.strokeWidth = 0;

                    // Add cursor
                    chart.cursor = new am4charts.XYCursor();
                    // Add scrollbar
                    let scrollbarX = new am4core.Scrollbar();
                    chart.scrollbarX = scrollbarX;
                    chart.scrollbarX.parent = chart.bottomAxesContainer;
                    chart.scrollbarX.startGrip.hide();
                    chart.scrollbarX.endGrip.hide();

                    chart.scrollbarX.start = 0;
                    chart.scrollbarX.end = 0.5;

                    chart.zoomOutButton = new am4core.ZoomOutButton();
                    chart.zoomOutButton.hide();

                    // Set chart padding to ensure scrollbar is visible
                    chart.paddingBottom = 50;
                });
            }
        },
        getColumnColor(index) {
            const colors = [
                "#C577B4",
                "#DE7C7C",
                "#4BAFA9",
                "#D9C168",
                "#7AAF4B",
                "#C577B4",
                "#D1D1D1",
                "#A7AC1B",
                "#909CD0",
            ];
            return colors[index % colors.length];
        },
    },
};
</script>

<style scoped>
#chartdiv {
    width: 100%;
    height: 500px;
    overflow-x: auto;
}
</style>
